import { useState } from 'react';
import {
  AccountModel,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  MauSoHoaDon,
  NhanVien,
  NhapXuat,
  PhuongThucGiaoHang,
  PhuongTienVanChuyen,
  QuyenChungTu,
  Tax
} from '@/types/schemas';

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  nhapXuat: NhapXuat | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;
  nhanVien: NhanVien | null;
  mauHoaDon: MauSoHoaDon | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  donViCoSo: DonViCoSo | null;

  // Other information
  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongThucGiaoHang: PhuongThucGiaoHang | null;
  cucThue: KhachHang | null;

  // Tax information
  taxModel: Tax | null;
  taiKhoanThue: AccountModel | null;

  // Other information
  soChungTu: string;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang) => void;
  setNhapXuat: (nhapXuat: NhapXuat) => void;
  setTaiKhoan: (taiKhoan: AccountModel | null) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;
  setTaxModel: (taxModel: Tax) => void;
  setTaiKhoanThue: (taiKhoanThue: AccountModel) => void;

  setNhanVien: (nhanVien: NhanVien) => void;
  setMauHoaDon: (mauHoaDon: MauSoHoaDon) => void;

  setSoChungTu: (soChungTu: string) => void;

  setDiaChi: (diaChi: DiaChi) => void;
  setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => void;
  setPhuongThucGiaoHang: (phuongThucGiaoHang: PhuongThucGiaoHang) => void;
  setCucThue: (cucThue: KhachHang) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  nhapXuat: null,
  taiKhoan: null,
  hanThanhToan: null,
  nhanVien: null,
  mauHoaDon: null,

  // Document information
  quyenChungTu: null,
  donViCoSo: null,
  diaChi: null,
  phuongTienVanChuyen: null,
  phuongThucGiaoHang: null,
  cucThue: null,

  // Tax information
  taxModel: null,
  taiKhoanThue: null,

  // Other information
  soChungTu: ''
};

function transformInitialData(initialData?: MuaHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Customer information
    khachHang: initialData.ma_kh_data || null,
    nhapXuat: initialData.ma_nx_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.ma_tt_data || null,

    // Document information
    quyenChungTu: initialData.ma_nk_data || null,
    donViCoSo: initialData.unit_id_data || null,
    diaChi: initialData.ma_dc_data || null,
    phuongTienVanChuyen: initialData.ma_ptvc_data || null,
    phuongThucGiaoHang: initialData.ma_ptgh_data || null,
    cucThue: initialData.ma_kh9_data || null,

    // Tax information
    taxModel: initialData.ma_thue_data || null,
    taiKhoanThue: initialData.tk_thue_data || null,

    // Other information
    soChungTu: initialData.so_ct || ''
  };
}

export function useFormFieldState(initialData?: MuaHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhapXuat: (nhapXuat: NhapXuat) => {
      setState(prev => ({
        ...prev,
        nhapXuat
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel | null) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setTaxModel: (taxModel: Tax) => {
      setState(prev => ({
        ...prev,
        taxModel
      }));
    },

    setTaiKhoanThue: (taiKhoanThue: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoanThue
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setMauHoaDon: (mauHoaDon: MauSoHoaDon) => {
      setState(prev => ({
        ...prev,
        mauHoaDon
      }));
    },

    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    setDiaChi: (diaChi: DiaChi) => {
      setState(prev => ({ ...prev, diaChi }));
    },

    setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => {
      setState(prev => ({ ...prev, phuongTienVanChuyen }));
    },

    setPhuongThucGiaoHang: (phuongThucGiaoHang: PhuongThucGiaoHang) => {
      setState(prev => ({ ...prev, phuongThucGiaoHang }));
    },

    setCucThue: (cucThue: KhachHang) => {
      setState(prev => ({ ...prev, cucThue }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
